import { Star, ShieldCheck, Flag, Users } from "lucide-react";
import Seo from "@/components/Seo";
import { FadeUp } from "@/components/Reveal";
import { QuoteButton } from "@/components/Layout";
import { MANIFESTO, IMAGES, BUSINESS } from "@/content/siteContent";

const BADGES = [
  { icon: Star, title: "Veteran Owned & Operated", text: "Founded and led by a US military veteran — service is in our DNA." },
  { icon: ShieldCheck, title: "Licensed & Insured", text: "Your property and our crew are protected on every single job." },
  { icon: Flag, title: "American Values", text: "Show up on time, do the job right, stand behind the work." },
  { icon: Users, title: "Community First", text: "Local hires, local charities, donation-first hauling." },
];

export default function About() {
  return (
    <>
      <Seo
        title="About Us — Veteran-Owned | Home Team Moving Co."
        siteName="Home Team Moving Co."
        description="Home Team Moving Co. is a veteran-owned moving and hauling company bringing military discipline to Southern California moves, cleanouts, and hauling jobs."
      />
      <section className="border-b border-white/10">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-2 lg:items-center lg:py-24">
          <FadeUp>
            <p className="eyebrow mb-4">Our Story</p>
            <h1 className="font-display text-4xl font-extrabold uppercase leading-none tracking-tight text-white sm:text-5xl lg:text-6xl">
              From serving the country to <span className="text-gold">serving the neighborhood.</span>
            </h1>
            <p className="mt-6 leading-relaxed text-slate-300">
              {BUSINESS.name} was founded on a simple idea: the discipline, teamwork, and accountability learned in the military are exactly what the moving industry needs. Too many families have been burned by no-shows, hidden fees, and careless crews. We built the company we wished existed.
            </p>
            <p className="mt-4 leading-relaxed text-slate-300">
              Today our veteran-led crews handle everything from full residential moves to estate cleanouts and commercial turnovers across Southern California — every job planned like an operation and finished like a promise.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="gold-frame">
            <img src={IMAGES.interior1} alt="A spotless, move-ready Southern California home interior" className="h-[460px] w-full object-cover" loading="lazy" />
          </FadeUp>
        </div>
      </section>

      <section className="border-b border-white/10 bg-navy-900/40">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <FadeUp>
            <p className="eyebrow mb-4">What We Stand For</p>
            <h2 className="font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">The standard we carry</h2>
          </FadeUp>
          <div className="mt-12 grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
            {BADGES.map((b, i) => (
              <FadeUp key={b.title} delay={i * 0.08} className="bg-navy-950 p-8">
                <b.icon className="h-7 w-7 text-gold" />
                <h3 className="mt-5 font-display text-lg font-bold uppercase tracking-wide text-white">{b.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{b.text}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:py-24">
          <FadeUp>
            <p className="eyebrow mb-4">The Mission</p>
            <h2 className="max-w-2xl font-display text-3xl font-bold uppercase tracking-tight text-white sm:text-4xl">
              Three chapters, one promise
            </h2>
          </FadeUp>
          <div className="mt-12 space-y-0">
            {MANIFESTO.map((m, i) => (
              <FadeUp key={m.chapter} delay={i * 0.1} className="grid gap-4 border-t border-white/10 py-10 sm:grid-cols-[120px_1fr_2fr] sm:items-baseline">
                <p className="text-outline font-display text-5xl font-extrabold leading-none">{m.chapter}</p>
                <h3 className="font-display text-xl font-bold uppercase tracking-wide text-gold">{m.title}</h3>
                <p className="text-sm leading-relaxed text-slate-300">{m.content}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp className="mt-12">
            <QuoteButton testId="about-quote-cta" className="px-9 py-4 text-base" />
          </FadeUp>
        </div>
      </section>
    </>
  );
}
